import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';

export const useTasks = () => {
  const { isAuthenticated, company } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTasks = useCallback(async (filters = {}) => {
    try {
      setLoading(true);

      const response = await api.get(`/api/companies/${company.id}/complaints`, {
        params: {
          // Support multiple statuses (array) as a comma-separated string for the API.
          status: Array.isArray(filters.status)
            ? (filters.status.length > 0 ? filters.status.join(',') : undefined)
            : filters.status,
          departmentId: filters.departmentId,
          employeeId: filters.employeeId,
          customerId: filters.customerId,
          supervisorId: filters.supervisorId,
          startDate: filters.startDate,
          endDate: filters.endDate
        }
      });
      
      if (response.data?.is_success) {
        setTasks(response.data.complaints);
        // Backend may return totalComplaints; otherwise derive from payload
        const backendCount = response.data.totalComplaints;
        setTotalCount(typeof backendCount === 'number' ? backendCount : (Array.isArray(response.data.complaints) ? response.data.complaints.length : 0));
        // Since we fetch all items now, backend pagination is irrelevant here
        setTotalPages(1);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [company?.id]);

  const createTask = useCallback(async (taskData) => {
    try {
      const payload = {
        companyId: company.id,
        description: taskData.description,
        departmentId: taskData.departmentId,
        location: taskData.location,
        images: taskData.images,
        customer: taskData.customer,
        taskType: taskData.taskType || 'task',
        participants: Array.isArray(taskData.participants) && taskData.participants.length > 0 ? taskData.participants : undefined,
        meetingDuration: typeof taskData.meetingDuration === 'number' ? taskData.meetingDuration : undefined,
        // Include dueDate if provided, converting to ISO format
        dueDate: taskData.dueDate && taskData.dueDate.trim() !== ''
          ? new Date(taskData.dueDate).toISOString()
          : null,
        // Convert followUpOn to full ISO string (date & time) if provided
        followUpOn: taskData.followUpOn && taskData.followUpOn.trim() !== ''
          ? new Date(taskData.followUpOn).toISOString()
          : null
      };

      if (taskData.employeeId && taskData.employeeId.trim() !== '') {
        payload.employeeId = taskData.employeeId;
        payload.supervisorNotes = 'Task created by supervisor';
      }

      // Add supervisor assignment if provided
      if (taskData.supervisorId && taskData.supervisorId.trim() !== '') {
        payload.supervisorId = taskData.supervisorId;
      }

      // Set status to pending when assignment is made at creation time
      if ((taskData.employeeId && taskData.employeeId.trim() !== '') || (taskData.supervisorId && taskData.supervisorId.trim() !== '')) {
        payload.status = 'pending';
      }

      console.log('Task creation payload:', payload); // Debug log

      const response = await api.post('/api/companies/complaints/create-on-behalf', payload);

      if (response.data?.is_success) {
        setTasks(prev => [response.data.complaint, ...prev]);
        return { success: true, task: response.data.complaint };
      } else {
        return { success: false, error: response.data?.message || 'Failed to create task' };
      }
    } catch (err) {
      console.error('Failed to create task:', err);
      return { success: false, error: 'Failed to create task' };
    }
  }, [company?.id]);

  const createSubtask = useCallback(async (parentTaskId, subtaskData) => {
    try {
      const payload = {
        description: subtaskData.description,
        departmentId: subtaskData.departmentId,
        employeeId: subtaskData.employeeId && subtaskData.employeeId.trim() !== '' ? subtaskData.employeeId : null,
        supervisorId: subtaskData.supervisorId && subtaskData.supervisorId.trim() !== '' ? subtaskData.supervisorId : null,
        priority: subtaskData.priority || 'medium',
        // Include dueDate in ISO format if provided
        dueDate: subtaskData.dueDate && subtaskData.dueDate.trim() !== ''
          ? new Date(subtaskData.dueDate).toISOString()
          : null,
        supervisorNotes: subtaskData.supervisorNotes || '',
        location: subtaskData.location || null,
        images: subtaskData.images || [],
        // Convert followUpOn to full ISO string (date & time) if provided
        followUpOn: subtaskData.followUpOn && subtaskData.followUpOn.trim() !== ''
          ? new Date(subtaskData.followUpOn).toISOString()
          : null
      };

      console.log('Subtask creation payload (hook):', payload); // Debug log

      const response = await api.post(`/api/complaints/${parentTaskId}/subtasks`, payload);

      if (response.data?.is_success) {
        // Update the parent task in the tasks list with the new subtask
        setTasks(prev => prev.map(task => {
          if (task.id === parentTaskId) {
            const updatedTask = { ...task };
            if (!updatedTask.subtasks) {
              updatedTask.subtasks = [];
            }
            updatedTask.subtasks = [...updatedTask.subtasks, response.data.subtask];
            return updatedTask;
          }
          return task;
        }));
        
        return { success: true, subtask: response.data.subtask };
      } else {
        return { success: false, error: response.data?.message || 'Failed to create subtask' };
      }
    } catch (err) {
      console.error('Failed to create subtask:', err);
      return { success: false, error: 'Failed to create subtask' };
    }
  }, []);

  const updateTask = useCallback(async (taskId, updateData) => {
    try {
      console.log('Update task payload before API call:', updateData); // Debug log

      const response = await api.put(`/api/complaints/${taskId}`, updateData);
      
      if (response.data?.is_success) {
        setTasks(prev => prev.map(task => 
          task.id === taskId ? response.data.complaint : task
        ));
        return { success: true, task: response.data.complaint };
      } else {
        return { success: false, error: response.data?.message || 'Failed to update task' };
      }
    } catch (err) {
      console.error('Error updating task:', err);
      return { success: false, error: 'Failed to update task' };
    }
  }, []);

  const refreshTasks = useCallback(() => {
    setCurrentPage(1);
    // fetchTasks will be called automatically due to useEffect dependency
  }, []);

  return {
    tasks,
    loading,
    error,
    currentPage,
    totalPages,
    isLoadingMore,
    setCurrentPage,
    fetchTasks,
    createTask,
    createSubtask,
    updateTask,
    refreshTasks,
    setTasks,
    totalCount
  };
}; 